import React, { useState, useEffect, useRef } from 'react';

import RequestCard from 'components/Cards/RequestCard';
import ConfirmationModal from 'components/ConfirmationModal';
import EmptyList from 'components/EmptyList';
import List from 'components/List';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { ConfirmMessage } from 'models/confirmMessage';
import { RepprovalReason } from 'models/repprovalReason';
import { RequestedItem } from 'models/requestHistory';
import { TravelAwaitingApprove } from 'models/travelApproved';
import { htmlT, extractErrorMessage } from 'utils/generic';

import ConfirmApproveModal from '../ConfirmApproveModal';
import OptionsItems from './OptionsItems';
import { TextModal, ListContainer } from './styles';

interface Props {
  data?: TravelAwaitingApprove[];
  onApprove: (travel: RequestedItem) => Promise<void>;
  onReject: (travel: RequestedItem, reason?: RepprovalReason) => Promise<void>;
  onConfirmApprove: () => void;
  onConfirmClose: () => void;
  onDetailsClose: () => void;
  showConfirm: boolean;
  confirmMessage: ConfirmMessage;
  showDetails?: string;
  reasons?: RepprovalReason[];
}

const ListAwaiting = ({
  data,
  onApprove,
  onReject,
  onConfirmApprove,
  onConfirmClose,
  showConfirm,
  confirmMessage,
  showDetails,
  onDetailsClose,
  reasons,
}: Props) => {
  const [approve, setApprove] = useState<RequestedItem>();
  const [reject, setReject] = useState<RequestedItem>();
  const [showApprove, setShowApprove] = useState(false);
  const [name, setName] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const reasonRef = useRef(null);
  const [selectedReason, setSelectedReason] = useState<RepprovalReason>();

  const toast = useToast();
  const { t } = useStoreTranslation();

  const handleActionClick = (type: 'approve' | 'decline', travel: RequestedItem) => {
    setName(travel.userName);
    if (type === 'approve') {
      setApprove(travel);
      setShowApprove(true);
    } else setReject(travel);
  };

  const handleConfirm = async (type: string, travel?: RequestedItem, reason?: RepprovalReason) => {
    if (!travel) return;

    setSubmitting(true);
    try {
      if (type === 'approve') {
        await onApprove(travel);
        setApprove(undefined);
      } else if (type === 'reject') {
        await onReject(travel, reason);
        setReject(undefined);
      }
      setName('');
    } catch (error) {
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmApprove = () => {
    setForceRefresh(true);
    if (onConfirmApprove) onConfirmApprove();
  };

  useEffect(() => {
    if (confirmed && !isSubmitting) {
      setConfirmed(false);
      setShowApprove(false);
    }
  }, [confirmed, isSubmitting]);

  const handleSelectReason = (reasonValue: RepprovalReason) => {
    setSelectedReason(reasonValue);
  };

  return (
    <ListContainer>
      {data?.length ? (
        <List grid>
          {data.map((element) => (
            <RequestCard
              key={element.travelId}
              item={element}
              approval
              onClick={(type: 'approve' | 'decline') => handleActionClick(type, element)}
              onConfirm={() => handleConfirm('approve', element)}
              onDecline={(id, reason) => handleConfirm('reject', element, reason)}
              onDetailsClose={onDetailsClose}
              submitting={isSubmitting}
              showDetails={showDetails === element.travelId}
              onRefreshed={() => setForceRefresh(false)}
              forceRefresh={forceRefresh}
              reasons={reasons}
            />
          ))}
        </List>
      ) : (
        <EmptyList
          description={t('dashboard.emptyAwaiting')}
          image="/images/empty-awaiting-requests.svg"
          backgroundImage="/images/background-empty-awaiting-requests.svg"
        />
      )}
      <ConfirmationModal
        open={!!reject}
        showCancelButton={true}
        title={t('travelApproval.modal.title')}
        onClose={() => {
          if (isSubmitting) return;
          setReject(undefined);
        }}
        onConfirm={() => handleConfirm('reject', reject, selectedReason)}
        submitting={isSubmitting}
        isModal={false}
      >
        <>
          <TextModal>{htmlT(t, 'travelApproval.modal.textReject', { name })}</TextModal>
          {reasons && (
            <>
              <br />
              <CustomSelect
                name="reprovalReasons"
                onChange={handleSelectReason}
                placeHolder={t('travelApproval.modal.selectReprovalReasonPlaceholder')}
                selectRef={reasonRef}
                value={selectedReason}
              >
                {({ ...selectProps }: CustomSelectRenderProps) => (
                  <OptionsItems {...selectProps} selectRef={reasonRef} reasons={reasons} />
                )}
              </CustomSelect>
            </>
          )}
        </>
      </ConfirmationModal>
      <ConfirmationModal
        open={showApprove}
        title={t('travelApproval.modal.title')}
        showCancelButton={true}
        onClose={() => {
          if (isSubmitting) return;
          setApprove(undefined);
          setShowApprove(false);
        }}
        onConfirm={() => {
          setConfirmed(true);
          handleConfirm('approve', approve);
        }}
        submitting={isSubmitting}
      >
        <TextModal>{htmlT(t, 'travelApproval.modal.textApprove', { name })}</TextModal>
      </ConfirmationModal>
      <ConfirmApproveModal
        onConfirmApprove={handleConfirmApprove}
        onConfirmClose={onConfirmClose}
        showConfirm={showConfirm}
        confirmMessage={confirmMessage}
        showCancelButton={true}
      ></ConfirmApproveModal>
    </ListContainer>
  );
};

export default ListAwaiting;
